<template>
  <div class="multi-file-uploader">
    <!-- 文件列表展示 -->
    <template v-if="modelValue?.length">
      <div v-for="(file, index) in modelValue" :key="index" class="file-item">
        <div class="w-[148px] h-[148px] relative">
          <!-- 图片文件 -->
          <template v-if="fileUtils.isImageFile(file?.raw || file?.url || file)">
            <img :src="fileUtils.getImagePreviewUrl(file?.raw || file?.url || file)"
              class="w-full h-full object-contain max-w-full" />
          </template>
          <!-- 非图片文件显示文件名 -->
          <template v-else>
            <div class="w-full h-full flex flex-col items-center justify-center p-2 text-blue-700">
              <AliIcon name="file" :size="iconSize" class="mb-1" />
              <span class="text-xs text-center truncate w-full" :title="fileUtils.getFileName(file?.raw || file?.url || file)">
                {{ fileUtils.getFileName(file?.raw || file?.url || file) }}
              </span>
            </div>
          </template>
          <!-- 操作按钮区域 - hover时显示 -->
          <div v-if="showDeleteButton || showPreviewButton" class="absolute top-1 right-1 flex items-center opacity-0 transition-opacity duration-300">
            <!-- 预览按钮 -->
            <el-button v-if="showPreviewButton" type="primary" size="small" circle @click.stop="handlePreview(file)">
              <AliIcon name="eye"></AliIcon>
            </el-button>
            <!-- 删除按钮 -->
            <el-button v-if="showDeleteButton" type="danger" size="small" circle @click.stop="deleteFile(index)">
              <AliIcon name="delete"></AliIcon>
            </el-button>
          </div>
        </div>
      </div>
    </template>
    
    <!-- 上传区域 -->
    <el-upload class="picture-uploader" drag :multiple="true" action="" :show-file-list="false" :auto-upload="false" :accept="accept.join(',')"
      :before-upload="beforeUpload" :on-change="handleChange">
      <div class="w-[148px] h-[148px] bg-[#fafafa] flex flex-col items-center justify-center gap-1 px-2 text-gray-500">
        <AliIcon name="upload" :size="uploadIconSize" />
        <span class="text-xs text-center text-gray-500" v-if="uploadText">{{uploadText}}</span>
        <span class="text-xs text-center text-gray-400">{{ tipText || `支持${getFormatTip()}格式，不超过${maxSize}MB` }}</span>
      </div>
    </el-upload>

    <!-- 图片预览对话框 -->
    <el-dialog v-model="dialogVisible" title="图片预览" width="98%" top="5vh" class="max-w-[800px]">
      <img :src="dialogImageUrl" class="preview-image w-full h-auto" alt="预览图片" />
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
  import { ref, watch } from 'vue';
  import { fileUtils } from '@/utils';
  import { getFormatTip as getCommonFormatTip, beforeUpload as commonBeforeUpload } from './uploadUtils';

  // 定义props
  interface Props {
    modelValue : (File | string)[];
    accept ?: string[];
    maxSize ?: number;
    iconSize ?: number;
    uploadIconSize ?: number;
    uploadText ?: string;
    tipText ?: string;
    showDeleteButton ?: boolean;
    showPreviewButton ?: boolean;
    maxCount ?: number; // 最大文件数量限制
  }

  const props = withDefaults(defineProps<Props>(), {
    modelValue: () => [],
    accept: () => ['.jpeg', '.jpg', '.png'],
    maxSize: 30,
    iconSize: 32,
    uploadIconSize: 26,
    tipText: '',
    showDeleteButton: true,
    showPreviewButton: true,
    maxCount: 20
  });

  // 定义emits
  const emit = defineEmits<{
    'update:modelValue' : [value: (File | string)[]];
    'change' : [files: (File | string)[]];
    'preview' : [file: File | string];
  }>();

  // 使用通用的文件格式提示文本函数
  const getFormatTip = () => getCommonFormatTip(props.accept);
  
  const fileList = ref<Array<{name: string, url?: string, raw?: File}>>([]);
  watch(() => props.modelValue, (newVal) => {
    if (newVal && Array.isArray(newVal)) {
      fileList.value = [...newVal];
    } else {
      fileList.value = [];
    }
  }, { immediate: true, deep: true });

  // 文件上传前校验 - 使用公共工具函数
  const beforeUpload = (file : File) => {
    // 检查文件数量限制
    if (fileList.value.length >= props.maxCount) {
      ElMessage.warning(`最多只能上传${props.maxCount}个文件`);
      return false;
    }
    
    // 调用公共的文件校验函数
    return commonBeforeUpload(file, props.maxSize, props.accept, getFormatTip());
  };

  // 文件改变处理
  const handleChange = (file : any) => {
    const rawFile = file.raw || file;
    if (beforeUpload(rawFile)) {
      fileList.value = [...fileList.value, rawFile];
      const newFiles = fileList.value.slice();
      emit('update:modelValue', newFiles);
      emit('change', newFiles);
    }
  };

  // 删除文件
  const deleteFile = (index : number) => {
    fileList.value = fileList.value.filter((_, i) => i !== index);
    const newFiles = fileList.value.slice();
    emit('update:modelValue', newFiles);
    emit('change', newFiles);
  };

  // 预览弹窗相关状态
  const dialogVisible = ref(false);
  const dialogImageUrl = ref('');

  // 文件预览处理
  const handlePreview = (file : File | string) => {
    if (file) {
      const previewFile = file?.url || file;
      emit('preview', previewFile);
      // 根据文件类型处理预览
      if (fileUtils.isImageFile(previewFile)) {
        // 图片文件显示弹窗
        dialogImageUrl.value = fileUtils.getImagePreviewUrl(previewFile);
        dialogVisible.value = true;
      } else {
        fileUtils.downloadFile({
          file: previewFile,
        });
      }
    }
  };
</script>

<style scoped>
  .multi-file-uploader {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }
  
  .file-item {
    position: relative;
  }
  
  .file-item {
    border: 1px dashed var(--el-border-color);
    border-radius: 6px;
    /* cursor: pointer; */
    position: relative;
    overflow: hidden;
    transition: var(--el-transition-duration-fast);
  }

  .file-item:hover {
    border-color: var(--el-color-primary);
  }
  .picture-uploader ::v-deep(.el-upload-dragger) {
    padding: 0!important;
  }
  .picture-uploader ::v-deep(.el-upload-dragger.is-dragover) {
    border-width: 1px!important;
  }

  /* 文件预览容器hover时显示操作按钮 */
  .file-item .relative:hover > div[class*="opacity-0"] {
    opacity: 1;
  }

  /* 预览图片样式 */
  .preview-image {
    max-height: 80vh;
    object-fit: contain;
  }
</style>